import { formatMoney, formatMonth } from '../domain/format';
import type { MonthlyReportRow, MonthlySummary, TreasurySettings } from '../domain/types';

const PAGE_W = 1080;
const MIN_PAGE_H = 1940;
const MARGIN = 72;
const CONTENT_W = PAGE_W - MARGIN * 2;
const NOTE_FONT_SIZE = 19;
const NOTE_LINE_HEIGHT = 28;

const COLORS = {
  paper: '#ffffff',
  surface: '#f4f6f8',
  border: '#dfe3e8',
  text: '#161a20',
  muted: '#6f7782',
  faint: '#8b929b',
  success: '#0f766e',
  danger: '#b42318'
};

type ReportPdfOptions = {
  churchName: string;
  display: TreasurySettings['reportDisplay'];
  month: string;
  summary: MonthlySummary;
};

type Fonts = {
  sans: string;
  mono: string;
};

type PreparedRow = MonthlyReportRow & {
  hideBalance: boolean;
  noteLines: string[];
};

export async function createMonthlyReportPdfBlob({ churchName, display, month, summary }: ReportPdfOptions) {
  const { PDFDocument } = await import('pdf-lib');
  await document.fonts?.ready;

  const fonts: Fonts = {
    sans: '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"Geist Mono", "SF Mono", "Roboto Mono", monospace'
  };

  const measureCanvas = document.createElement('canvas');
  const measureContext = requireCanvasContext(measureCanvas);
  const preparedRows = prepareRows(measureContext, fonts, summary.rows, display);
  const pageHeight = calculatePageHeight(preparedRows);

  const canvas = document.createElement('canvas');
  canvas.width = PAGE_W;
  canvas.height = pageHeight;

  const ctx = requireCanvasContext(canvas);
  ctx.fillStyle = COLORS.paper;
  ctx.fillRect(0, 0, PAGE_W, pageHeight);
  ctx.textBaseline = 'alphabetic';

  drawReport(ctx, fonts, {
    churchName,
    display,
    month,
    pageHeight,
    preparedRows,
    summary
  });

  const imageBlob = await canvasToBlob(canvas);
  const imageBytes = new Uint8Array(await imageBlob.arrayBuffer());
  const pdf = await PDFDocument.create();
  pdf.setTitle(`${churchName || 'SGRBC'} Monthly finance report - ${formatMonth(month)}`);
  pdf.setSubject('Monthly church finance report');
  pdf.setCreator('SGRBC Treasury');
  pdf.setProducer('SGRBC Treasury');

  const page = pdf.addPage([PAGE_W, pageHeight]);
  const image = await pdf.embedPng(imageBytes);
  page.drawImage(image, { x: 0, y: 0, width: PAGE_W, height: pageHeight });

  const bytes = await pdf.save();
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new Blob([buffer], { type: 'application/pdf' });
}

function drawReport(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  {
    churchName,
    display,
    month,
    pageHeight,
    preparedRows,
    summary
  }: ReportPdfOptions & { pageHeight: number; preparedRows: PreparedRow[] }
) {
  const cashAndBankClosing = summary.cashClosingBalance + summary.bankClosingBalance;

  drawLabel(ctx, fonts, churchName || 'SGRBC', MARGIN, 80, 17, 1.15);
  drawText(ctx, fonts, 'Monthly finance report', MARGIN, 134, 48, 400);
  drawText(ctx, fonts, formatMonth(month), MARGIN, 178, 30, 400, COLORS.muted);

  const summaryTop = 256;
  ctx.fillStyle = COLORS.surface;
  ctx.fillRect(MARGIN, summaryTop, CONTENT_W, 304);
  drawLabel(ctx, fonts, 'Summary', MARGIN + 28, summaryTop + 42, 17, 1.15);
  drawText(ctx, fonts, 'Cash and bank closing', MARGIN + 28, summaryTop + 88, 28, 400, COLORS.faint);
  drawRight(ctx, fonts, formatPdfMoney(cashAndBankClosing), PAGE_W - MARGIN - 28, summaryTop + 88, 42, 700);
  drawRule(ctx, summaryTop + 124, MARGIN + 28, CONTENT_W - 56);
  drawMetric(ctx, fonts, 'Total received', summary.totalMonthlyIncome, summaryTop + 164, 28);
  drawMetric(ctx, fonts, 'Total spent', summary.cashExpenses + summary.bankExpenses, summaryTop + 204, 28);
  drawRule(ctx, summaryTop + 232, MARGIN + 28, CONTENT_W - 56);
  drawMetric(ctx, fonts, 'Mission Fund balance', summary.missionFundClosingBalance, summaryTop + 268, 28, COLORS.success);

  drawLabel(ctx, fonts, 'Account detail', MARGIN, 644, 18, 1.15);

  let y = 710;
  preparedRows.forEach((row) => {
    y = drawAccountSection(ctx, fonts, row, display, y);
  });

  if (pageHeight > MIN_PAGE_H) {
    drawRule(ctx, pageHeight - 72, MARGIN, CONTENT_W, '#eef1f4');
  }
}

function drawAccountSection(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  row: PreparedRow,
  display: TreasurySettings['reportDisplay'],
  y: number
) {
  drawText(ctx, fonts, row.account, MARGIN, y, 33, 500);
  y += 48;
  drawRule(ctx, y);

  y += 40;
  drawMetric(ctx, fonts, 'Closing balance', row.balance, y, 27, moneyTone(row.balance), row.hideBalance);
  y += 43;
  drawMetric(ctx, fonts, 'Received', row.income, y, 27);
  y += 43;
  drawMetric(ctx, fonts, 'Spent', row.expense, y, 27);
  y += 43;

  if (display.showNotesColumn && row.noteLines.length) {
    y += 2;
    drawRule(ctx, y);
    y += 40;
    drawLabel(ctx, fonts, 'Note', MARGIN, y, 16, 1.15);
    drawWrappedLines(ctx, fonts, row.noteLines, MARGIN + 96, y, NOTE_FONT_SIZE, NOTE_LINE_HEIGHT, 400, COLORS.muted);
    y += Math.max(34, row.noteLines.length * NOTE_LINE_HEIGHT);
  }

  y += 44;
  return y + 56;
}

function drawMetric(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  label: string,
  value: number,
  y: number,
  valueSize: number,
  valueColor = COLORS.text,
  hidden = false
) {
  drawLabel(ctx, fonts, label, MARGIN + (y < 600 ? 28 : 0), y, 16, 1.15);
  if (hidden) {
    drawRight(ctx, fonts, 'HIDDEN', PAGE_W - MARGIN, y - 2, 16, 700, COLORS.faint, fonts.sans);
    return;
  }
  const rightEdge = y < 600 ? PAGE_W - MARGIN - 28 : PAGE_W - MARGIN;
  drawRight(ctx, fonts, formatPdfMoney(value), rightEdge, y - 2, valueSize, 700, valueColor);
}

function drawText(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  value: string,
  x: number,
  y: number,
  size: number,
  weight = 400,
  color = COLORS.text,
  family = fonts.sans
) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px ${family}`;
  ctx.fillText(value, x, y);
}

function drawRight(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  value: string,
  x: number,
  y: number,
  size: number,
  weight = 600,
  color = COLORS.text,
  family = fonts.mono
) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px ${family}`;
  ctx.fillText(value, x - ctx.measureText(value).width, y);
}

function drawLabel(ctx: CanvasRenderingContext2D, fonts: Fonts, value: string, x: number, y: number, size: number, tracking: number) {
  drawTracked(ctx, value.toUpperCase(), x, y, size, 600, COLORS.muted, fonts.sans, tracking);
}

function drawTracked(
  ctx: CanvasRenderingContext2D,
  value: string,
  x: number,
  y: number,
  size: number,
  weight: number,
  color: string,
  family: string,
  tracking: number
) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${size}px ${family}`;
  let cursor = x;
  Array.from(value).forEach((letter) => {
    ctx.fillText(letter, cursor, y);
    cursor += ctx.measureText(letter).width + tracking;
  });
}

function drawWrappedLines(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  lines: string[],
  x: number,
  y: number,
  size: number,
  lineHeight: number,
  weight: number,
  color: string
) {
  lines.forEach((line, index) => {
    drawText(ctx, fonts, line, x, y + index * lineHeight, size, weight, color);
  });
}

function drawRule(ctx: CanvasRenderingContext2D, y: number, x = MARGIN, width = CONTENT_W, color = COLORS.border) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, width, 1);
}

function prepareRows(
  ctx: CanvasRenderingContext2D,
  fonts: Fonts,
  rows: MonthlyReportRow[],
  display: TreasurySettings['reportDisplay']
): PreparedRow[] {
  return rows.map((row) => {
    const shouldShowNote = display.showNotesColumn && row.note.trim();
    return {
      ...row,
      hideBalance: !display.showBalanceColumn || !row.showBalance,
      noteLines: shouldShowNote ? wrapText(ctx, fonts, row.note.trim(), CONTENT_W - 96, NOTE_FONT_SIZE, 400) : []
    };
  });
}

function calculatePageHeight(rows: PreparedRow[]) {
  let y = 710;
  rows.forEach((row) => {
    y += 48 + 40 + 43 * 3;
    if (row.noteLines.length) y += 2 + 40 + Math.max(34, row.noteLines.length * NOTE_LINE_HEIGHT);
    y += 44 + 56;
  });
  return Math.max(MIN_PAGE_H, Math.ceil(y + 96));
}

function wrapText(ctx: CanvasRenderingContext2D, fonts: Fonts, value: string, width: number, size: number, weight: number) {
  ctx.font = `${weight} ${size}px ${fonts.sans}`;
  const words = value.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width <= width) {
      current = next;
      return;
    }

    if (current) lines.push(current);
    current = word;
  });

  if (current) lines.push(current);
  return lines;
}

function formatPdfMoney(value: number) {
  return formatMoney(value).replace(/\s+/g, ' ');
}

function moneyTone(value: number) {
  if (value < 0) return COLORS.danger;
  if (value > 0) return COLORS.success;
  return COLORS.text;
}

function requireCanvasContext(canvas: HTMLCanvasElement) {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create report canvas.');
  return context;
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not create report image.'));
    }, 'image/png');
  });
}
